import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Menu, MenuItem, MenuList, Paper, Popper, Tooltip, Divider } from '@mui/material';
import { MoreHorizontal, Pencil, Trash2, X, Plus, Save, Info, AlertTriangle, Check, User, Eye, UserRoundPlus, Link, ClipboardPaste, Send, Download, Share2, FastForward, CalendarClock, ChevronRight } from 'lucide-react';

type PredefinedIconName = 'edit' | 'delete' | 'close' | 'add' | 'save' | 'info' | 'warning' | 'check' | 'user' | 'eye' | 'invite' | 'link' | 'paste' | 'download' | 'send' | 'share' | 'fastForward' | 'calendar';

type Action = {
	label: string;
	// Omitted (or ignored) when `submenu` is set - clicking/hovering opens
	// the flyout instead of firing an action directly.
	onClick?: () => void;
	icon?: PredefinedIconName | React.ReactNode;
	disabled?: boolean;
	// Shown on hover - most useful paired with `disabled` to explain why an
	// action can't be taken right now.
	tooltip?: string;
	// Renders this item as a second-layer flyout menu, opened on hover (and
	// on click, so it also works on touch) instead of calling onClick.
	submenu?: Action[];
};
const iconMap: Record<PredefinedIconName, React.ReactNode> = {
    edit: <Pencil size={18} />,
    delete: <Trash2 size={18} />,
    close: <X size={18} />,
    add: <Plus size={18} />,
    save: <Save size={18} />,
    info: <Info size={18} />,
    warning: <AlertTriangle size={18} />,
    check: <Check size={18} />,
	user: <User size={18} />,
	eye: <Eye size={18} />,
	invite: <UserRoundPlus size={18} />,
	link: <Link size={18} />,
	paste: <ClipboardPaste size={18} />,
	download: <Download size={18} />,
	send: <Send size={18} />,
	share: <Share2 size={18} />,
	fastForward: <FastForward size={18} />,
	calendar: <CalendarClock size={18} />,
};

// A row-status badge (Linked/Related/Moved/etc) - purely informational, not
// an action. When present, the trigger button shows these instead of the
// plain ellipsis (cycling through them if there's more than one) as a
// glanceable summary of the row's state, and the opened menu repeats the
// full set at the top so hovering each still reveals what it means.
type StatusIcon = {
	icon: React.ReactNode;
	tooltip?: string;
};

type ActionMenuProps = {
	actions: Action[];
	statusIcons?: StatusIcon[];
};

// How long each status icon stays showing before cycling to the next, when
// there's more than one - slow enough to actually read, not so slow it reads
// as stuck on the wrong one.
const STATUS_ICON_CYCLE_MS = 1800;

const ActionMenu: React.FC<ActionMenuProps> = ({ actions, statusIcons = [] }) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [submenu, setSubmenu] = useState<{ anchor: HTMLElement; actions: Action[] } | null>(null);
	const submenuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const open = Boolean(anchorEl);
	const [statusIconIndex, setStatusIconIndex] = useState(0);

	useEffect(() => {
		if (statusIcons.length <= 1) return;
		setStatusIconIndex(0);
		const interval = setInterval(() => {
			setStatusIconIndex(i => (i + 1) % statusIcons.length);
		}, STATUS_ICON_CYCLE_MS);
		return () => clearInterval(interval);
	}, [statusIcons.length]);

	const visibleActions = actions.filter(action => typeof action.onClick === 'function' || !!action.submenu?.length);
	// If every action in this menu is blocked, there's nothing worth opening it
	// for - disable the trigger itself instead of making the user open a menu
	// full of greyed-out items.
	const allDisabled = visibleActions.length > 0 && visibleActions.every(action => action.disabled);
	// Each disabled action can carry its own reason (e.g. "already linked to
	// a provision") - prefer that single reason over a generic message, which
	// is misleading whenever the real cause has nothing to do with
	// permissions. Falls back to a neutral "nothing to do here" message
	// (not a permission claim) when there's no single specific reason to show.
	const specificReasons = Array.from(new Set(visibleActions.map(a => a.tooltip).filter(Boolean)));
	const disabledTooltip = specificReasons.length === 1 ? specificReasons[0] : 'No actions available for this row';

	const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
		setSubmenu(null);
	};
	const cancelSubmenuClose = () => {
		if (submenuCloseTimer.current) {
			clearTimeout(submenuCloseTimer.current);
			submenuCloseTimer.current = null;
		}
	};
	// Small delay before closing so the cursor can travel diagonally from
	// the parent item into the flyout without it disappearing underneath it.
	const scheduleSubmenuClose = () => {
		cancelSubmenuClose();
		submenuCloseTimer.current = setTimeout(() => setSubmenu(null), 200);
	};

	return (
		<Box display="flex" alignItems="center" justifyContent="flex-end">
			<Tooltip title={allDisabled ? disabledTooltip : ''}>
				<span>
					<IconButton
						onClick={handleOpen}
						disabled={allDisabled}
						sx={{
							border: `1px solid ${allDisabled ? 'var(--color-text-muted)' : 'var(--color-primary)'}`,
							borderRadius: 1,
							width: 25,
							height: 25,
							padding: 0,
							backgroundColor: allDisabled ? 'transparent' : 'rgba(var(--color-primary-rgb), 0.1529)',
							opacity: allDisabled ? 0.5 : 1,
						}}
					>
						{statusIcons.length > 0 ? statusIcons[statusIconIndex % statusIcons.length].icon : (
							<MoreHorizontal size={18} color={allDisabled ? 'var(--color-text-muted)' : 'var(--color-primary)'} />
						)}
					</IconButton>
				</span>
			</Tooltip>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				{statusIcons.length > 0 && [
					<Box key="status-icons" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 1, px: 2 }}>
						{statusIcons.map((s, i) => (
							s.tooltip ? (
								<Tooltip key={i} title={s.tooltip}>
									<Box display="flex" alignItems="center">{s.icon}</Box>
								</Tooltip>
							) : (
								<Box key={i} display="flex" alignItems="center">{s.icon}</Box>
							)
						))}
					</Box>,
					<Divider key="status-icons-divider" />,
				]}
				{actions
					.filter(action => typeof action.onClick === 'function' || !!action.submenu?.length)
					.map((action, idx, filteredActions) => {
						const hasSubmenu = !!action.submenu?.length;
						const item = (
							<MenuItem
								disabled={action.disabled}
								divider={idx < filteredActions.length - 1}
								sx={{ py: 1.25, px: 2 }}
								onMouseEnter={hasSubmenu ? (e) => {
									cancelSubmenuClose();
									setSubmenu({ anchor: e.currentTarget, actions: action.submenu! });
								} : undefined}
								onMouseLeave={hasSubmenu ? scheduleSubmenuClose : undefined}
								onClick={(e) => {
									if (action.disabled) return;
									if (hasSubmenu) {
										// Also open on click (not just hover) so this works on touch.
										setSubmenu({ anchor: e.currentTarget, actions: action.submenu! });
										return;
									}
									action.onClick?.();
									handleClose();
								}}
							>
								{action.icon && (
									<Box mr={1} display="inline-flex">
										{typeof action.icon === 'string' && iconMap[action.icon as PredefinedIconName]
											? iconMap[action.icon as PredefinedIconName]
											: action.icon}
									</Box>
								)}
								<Box flex={1}>{action.label}</Box>
								{hasSubmenu && (
									<Box ml={1} display="inline-flex">
										<ChevronRight size={16} />
									</Box>
								)}
							</MenuItem>
						);
							// Always wrap in the same plain span, whether or not this item has
							// a tooltip - having only tooltip-bearing items wear this extra
							// span threw their MenuItem's padding/alignment off from their
							// plain siblings. Tooltip itself is still only added when there's
							// actually a title, so items without one don't pick up its (denser-
							// looking) touch/hover handling for no reason. Tooltip needs a
							// listenable child - disabled MenuItems don't fire pointer events,
							// hence the span. The separator between items is MenuItem's own
							// `divider` prop above, not a separate <Divider> - one less place
							// for its spacing to drift from the submenu's.
							const wrapped = <span style={{ display: 'block' }}>{item}</span>;
							return action.tooltip ? (
								<Tooltip key={action.label + idx} title={action.tooltip} placement="left">{wrapped}</Tooltip>
							) : (
								<span key={action.label + idx} style={{ display: 'block' }}>{item}</span>
							);
					})}
			</Menu>
			{/* A second nested <Menu> here would be its own Modal, and a Modal's
			    (invisible) backdrop and scroll-lock reflow both sit/act on top of
			    the page behind it - including the parent "Delete" item this is
			    anchored to - which either stole its hover or nudged the layout
			    under the cursor the instant this opened, closing this, which
			    un-covered/un-shifted the item, reopening it, forever. Popper has
			    neither a backdrop nor scroll-lock, so it can't interfere with the
			    trigger it's anchored to; outside clicks still close everything via
			    the parent Menu's own backdrop, which un-anchors this too. */}
			<Popper
				open={!!submenu}
				anchorEl={submenu?.anchor ?? null}
				placement="left-start"
				style={{ zIndex: 1400 }}
			>
				<Paper
					elevation={4}
					onMouseEnter={cancelSubmenuClose}
					onMouseLeave={scheduleSubmenuClose}
				>
					<MenuList>
						{(submenu?.actions ?? []).map((subAction, idx, subActions) => (
							<MenuItem
								key={subAction.label + idx}
								disabled={subAction.disabled}
								divider={idx < subActions.length - 1}
								sx={{ py: 1.25, px: 2 }}
								onClick={() => {
									if (subAction.disabled) return;
									subAction.onClick?.();
									handleClose();
								}}
							>
								{subAction.icon && (
									<Box mr={1} display="inline-flex">
										{typeof subAction.icon === 'string' && iconMap[subAction.icon as PredefinedIconName]
											? iconMap[subAction.icon as PredefinedIconName]
											: subAction.icon}
									</Box>
								)}
								{subAction.label}
							</MenuItem>
						))}
					</MenuList>
				</Paper>
			</Popper>
		</Box>
	);
};

export default ActionMenu;
